import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Container,
    Grid,
    Button,
    FormControl,
    FormControlLabel,
    Checkbox,
    Typography,
    TextField,
    MenuItem,
    Card,
    CardContent,
    Box,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { GetNotificationRules, CreateNotificationRule, UpdateNotificationRule, DeleteNotificationRule, GetPriorities } from '../../../../api';
import { MODALITY_OPTIONS } from '../../../../constants/Consult';

const TYPES = {
    email: 'Email',
    text: 'Text Message'
};

const formatTimezone = (timezone) => {
    const tz = timezone || 'America/Los_Angeles';
    const friendlyNames = {
        'America/Los_Angeles': 'Pacific Time (PT)',
        'America/New_York': 'Eastern Time (ET)',
        'America/Chicago': 'Central Time (CT)',
        'America/Denver': 'Mountain Time (MT)',
        'America/Phoenix': 'Arizona Time (MST)',
        'America/Anchorage': 'Alaska Time (AKST)',
        'Pacific/Honolulu': 'Hawaii Time (HST)'
    };
    return friendlyNames[tz] || tz.replace('_', ' ').replace('America/', '').replace('/', ' / ');
};

const Notifications = ({ user, access, hasSchedule, hasModalitySchedule }) => {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [customPriorities, setCustomPriorities] = useState(null);
    const EVENTS = useMemo(() => ({
        ...(access?.consult && {
            onConsultUnclaimed: 'A new study received for consult is unclaimed',
            onConsultIncomplete: 'A new study received for consult is incomplete'
        }),
        ...(access?.consult && !customPriorities && access?.org?.priorities?.includes(10) && {
            onConsultUnclaimedSTAT: 'A new STAT study received for consult is unclaimed',
            onConsultIncompleteSTAT: 'A new STAT study for consult is incomplete'
        }),
        ...(( hasSchedule || hasModalitySchedule) && {
            onMobileUnscheduled: 'A mobile study is unscheduled'
        })
    }), [access?.consult, access?.org?.priorities, customPriorities, hasSchedule, hasModalitySchedule]);
    const [showForm, setShowForm] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [formData, setFormData] = useState({
        type: 'email',
        event: 'onConsultUnclaimed',
        priorityRef: null,
        modalities: [],
        time: 30,
        requestee: false,
        schedule: {
            enabled: false,
            days: [1, 2, 3, 4, 5], // Monday-Friday
            startTime: '09:00',
            endTime: '17:00'
        }
    });


    const [modalityOpen, setModalityOpen] = useState(false);
    const currentOrg = access?.org;

    const loadRules = useCallback(async () => {
        try {
            const rules = await GetNotificationRules(currentOrg.id);
            setRules(rules);
            GetPriorities(currentOrg.id).then(cp => setCustomPriorities(cp?.length ? cp : null)).catch(() => setCustomPriorities(null));
        } catch (error) {
            console.error('Failed to load notification rules:', error);
        } finally {
            setLoading(false);
        }
    }, [currentOrg]);

    useEffect(() => {
        if (currentOrg) {
            loadRules();
        }
    }, [currentOrg, loadRules]);

    useEffect(() => {
        if (Object.keys(EVENTS).length === 0) {
            resetForm();
        }
    }, [EVENTS]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        try {
            const data = { ...formData, orgId: currentOrg.id };
            
            // Ensure complete schedule object when enabled
            if (data.schedule?.enabled) {
                data.schedule = {
                    enabled: true,
                    days: data.schedule.days || [1, 2, 3, 4, 5],
                    startTime: data.schedule.startTime || '09:00',
                    endTime: data.schedule.endTime || '17:00'
                };
            }
            
            
            if (editingRule) {
                await UpdateNotificationRule(editingRule.id, data);
            } else {
                await CreateNotificationRule(data);
            }
            await loadRules();
            resetForm();
        } catch (error) {
            console.error('Failed to save notification rule:', error);
        }
    }, [editingRule, formData, currentOrg, loadRules]);

    const handleDelete = async (ruleId) => {
        if (!window.confirm('Are you sure you want to delete this notification rule?')) return;
        try {
            await DeleteNotificationRule(ruleId);
            // Close edit form if deleting the rule being edited
            if (editingRule && editingRule.id === ruleId) {
                resetForm();
            }
            await loadRules();
        } catch (error) {
            console.error('Failed to delete notification rule:', error);
        }
    };

    const handleEdit = (rule) => {
        setEditingRule(rule);
        const schedule = rule.schedule || {
            enabled: false,
            days: [1, 2, 3, 4, 5],
            startTime: '09:00',
            endTime: '17:00'
        };
        setFormData({
            type: rule.type,
            event: rule.event,
            priorityRef: rule.priorityRef || null,
            modalities: rule.modalities || [],
            time: rule.time,
            requestee: rule.requestee,
            schedule
        });

        setShowForm(true);
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingRule(null);

        setFormData({
            type: 'email',
            event: 'onConsultUnclaimed',
            priorityRef: null,
            modalities: [],
            time: 30,
            requestee: false,
            schedule: {
                enabled: false,
                days: [1, 2, 3, 4, 5],
                startTime: '09:00',
                endTime: '17:00'
            }
        });
    };

    if (!currentOrg) return (
        <Container>
            <Typography color="textSecondary">Please select an organization to manage notification settings.</Typography>
        </Container>
    );

    if (loading) return (
        <Container>
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        </Container>
    );

    return (
        <Container>
            <Grid container sx={{ mb: 3 }} spacing={3} justifyContent="space-between" alignItems="center">
                <Grid item>
                    <Typography component="h1" variant="h4">Notification Settings for {currentOrg.name}</Typography>
                </Grid>
                {Object.keys(EVENTS).length > 0 && (
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => setShowForm(true)}
                        >
                            Add Notification Rule
                        </Button>
                    </Grid>
                )}
            </Grid>

            {showForm && Object.keys(EVENTS).length > 0 && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                            {editingRule ? 'Edit' : 'Add'} Notification Rule for {currentOrg?.name}
                        </Typography>
                        <form onSubmit={handleSubmit} noValidate>
                            <Grid container sx={{ mb: 3 }} spacing={3} justifyContent="left" maxWidth="sm">
                                <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                    <TextField
                                        name="type"
                                        value={formData.type}
                                        label="Notification Type"
                                        onChange={(e) => setFormData({...formData, type: e.target.value})}
                                        variant="standard"
                                        select
                                    >
                                        {Object.entries(TYPES).map(([key, label]) => {
                                            if (key === 'email' && !user.email) return null;
                                            if (key === 'text' && !user.phone) return null;
                                            return <MenuItem key={key} value={key}>{label}</MenuItem>;
                                        })}
                                    </TextField>
                                </FormControl>
                                <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                    <TextField
                                        name="event"
                                        value={formData.event}
                                        label="Event"
                                        onChange={(e) => setFormData({...formData, event: e.target.value})}
                                        variant="standard"
                                        select
                                    >
                                        {Object.entries(EVENTS).map(([key, description]) => (
                                            <MenuItem key={key} value={key}>{description}</MenuItem>
                                        ))}
                                    </TextField>
                                </FormControl>
                                <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                    <TextField
                                        label="Modalities (optional — leave blank for all)"
                                        variant="standard"
                                        select
                                        SelectProps={{
                                            multiple: true,
                                            open: modalityOpen,
                                            onOpen: () => setModalityOpen(true),
                                            onClose: () => setModalityOpen(false),
                                            renderValue: (selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map(v => <Chip key={v} label={v} size="small" onDelete={() => setFormData(f => ({...f, modalities: f.modalities.filter(m => m !== v)}))} onMouseDown={e => e.stopPropagation()} />)}
                                                </Box>
                                            )
                                        }}
                                        value={formData.modalities}
                                        onChange={(e) => { setFormData({...formData, modalities: e.target.value}); setModalityOpen(false); }}
                                    >
                                        {MODALITY_OPTIONS.map(mod => (
                                            <MenuItem key={mod} value={mod}>{mod}</MenuItem>
                                        ))}
                                    </TextField>
                                </FormControl>
                                {customPriorities && (
                                    <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                        <TextField
                                            name="priorityRef"
                                            value={formData.priorityRef || ''}
                                            label="Only for Priority (optional)"
                                            onChange={(e) => setFormData({...formData, priorityRef: e.target.value || null})}
                                            variant="standard"
                                            select
                                        >
                                            <MenuItem value=""><em>All priorities</em></MenuItem>
                                            {customPriorities.sort((a, b) => a.sortOrder - b.sortOrder).map(p => (
                                                <MenuItem key={p.id} value={p.id}>{p.name} and above</MenuItem>
                                            ))}
                                        </TextField>
                                    </FormControl>
                                )}
                                <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                                    <TextField
                                        name="time"
                                        value={formData.time}
                                        label="Delay (minutes)"
                                        onChange={(e) => setFormData({...formData, time: e.target.value || ''})}
                                        variant="standard"
                                        type="number"
                                        inputProps={{ min: 0 }}
                                        required
                                    />
                                </FormControl>
                                {currentOrg.users?.length && !currentOrg.settings?.disableRequestee ? (
                                    <FormControl sx={{ mb: 1 }} fullWidth>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={formData.requestee}
                                                    onChange={(e) => setFormData({...formData, requestee: e.target.checked})}
                                                />
                                            }
                                            label="Only notify me of studies available to me"
                                        />
                                    </FormControl>
                                ):''}
                            </Grid>
                            
                            <Accordion 
                                sx={{ mb: 2 }}
                                expanded={formData.schedule.enabled}
                                onChange={(e, expanded) => {
                                    setFormData({
                                        ...formData,
                                        schedule: { ...formData.schedule, enabled: expanded }
                                    });
                                }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>Schedule (Optional)</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Days of the week:</Typography>
                                    <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                                            <Chip
                                                key={day}
                                                label={day}
                                                clickable
                                                color={formData.schedule.days.includes(index) ? 'primary' : 'default'}
                                                onClick={() => {
                                                    const days = formData.schedule.days.includes(index)
                                                        ? formData.schedule.days.filter(d => d !== index)
                                                        : [...formData.schedule.days, index];
                                                    setFormData({
                                                        ...formData,
                                                        schedule: { ...formData.schedule, days }
                                                    });
                                                }}
                                            />
                                        ))}
                                    </Box>
                                    
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Start Time"
                                                type="time"
                                                value={formData.schedule.startTime}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    schedule: { ...formData.schedule, startTime: e.target.value }
                                                })}
                                                fullWidth
                                                variant="standard"
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="End Time"
                                                type="time"
                                                value={formData.schedule.endTime}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    schedule: { ...formData.schedule, endTime: e.target.value }
                                                })}
                                                fullWidth
                                                variant="standard"
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                                        Times are in {formatTimezone(currentOrg?.timezone)}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                            
                            <Box sx={{ mt: 3 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    startIcon={<SaveIcon />}
                                    sx={{ mr: 1 }}
                                >
                                    {editingRule ? 'Update' : 'Create'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={resetForm}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </form>
                    </CardContent>
                </Card>
            )}

            <Grid container spacing={2}>
                {rules.length === 0 ? (
                    <Grid item xs={12}>
                        <Typography color="textSecondary">No notification rules configured.</Typography>
                    </Grid>
                ) : (
                    rules.map(rule => (
                        <Grid item xs={12} key={rule.id}>
                            <Card>
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                        <Box>
                                            <Typography variant="h6" component="h3">
                                                {rule.orgId.name}
                                            </Typography>
                                            <Typography color="textSecondary" gutterBottom>
                                                {EVENTS[rule.event]}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {TYPES[rule.type]} • {rule.time} minutes delay{rule.modalities?.length ? ` • ${rule.modalities.join(', ')}` : ''}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Button
                                                size="small"
                                                startIcon={<EditIcon />}
                                                onClick={() => handleEdit(rule)}
                                                sx={{ mr: 1 }}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="small"
                                                color="error"
                                                startIcon={<DeleteIcon />}
                                                onClick={() => handleDelete(rule.id)}
                                            >
                                                Delete
                                            </Button>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                )}
            </Grid>
        </Container>
    );
};

export default Notifications;